import { useState } from "react";
import { CalendarClock, EyeOff, Save, Youtube } from "lucide-react";
import { toast } from "sonner";
import { patchCandidate } from "@/lib/scoutApi";
import { extractApiError } from "@/lib/api";

const ET = "America/New_York";
const FORMATS = [["", "Format…"], ["remote", "Remote"], ["studio", "In-studio"], ["phone", "Phone"]];

// recording_at is stored as a naive ET wall-clock "YYYY-MM-DDTHH:MM".
export const recordingStatus = (recordingAt) => {
  if (!recordingAt) return { tone: "warn", text: "No recording date yet" };
  const local = new Date(recordingAt.slice(0, 16));
  const nowEt = new Date(new Date().toLocaleString("en-US", { timeZone: ET }));
  const label = local.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) + " ET";
  const diffMin = Math.round((local - nowEt) / 60000);
  if (diffMin > -60 && diffMin <= 0) return { tone: "live", text: `Recording now · ${label}` };
  if (diffMin <= -60) return { tone: "past", text: `Recorded · ${label}` };
  const days = Math.floor(diffMin / 1440);
  const hours = Math.floor((diffMin % 1440) / 60);
  const rel = days > 0 ? `in ${days} day${days === 1 ? "" : "s"}` : hours > 0 ? `in ${hours}h` : `in ${diffMin}m`;
  return { tone: "ok", text: `Recording ${rel} · ${label}` };
};

const TONE = {
  warn: "text-amber-700 bg-amber-50 border-amber-200",
  live: "text-white bg-[var(--accent)] border-[var(--accent)]",
  past: "text-slate-600 bg-slate-100 border-slate-200",
  ok: "text-[#0A192F] bg-[var(--accent)]/10 border-[var(--accent)]/40",
};

export const RecordingPanel = ({ c, onChange }) => {
  const [when, setWhen] = useState(c.recording_at ? c.recording_at.slice(0, 16) : "");
  const [format, setFormat] = useState(c.recording_format || "");
  const [url, setUrl] = useState(c.episode_url || "");
  const [blurb, setBlurb] = useState(c.public_blurb || "");
  const [busy, setBusy] = useState(false);

  if (c.status !== "booked") return null;

  const dirty =
    when !== (c.recording_at ? c.recording_at.slice(0, 16) : "") ||
    format !== (c.recording_format || "") ||
    url !== (c.episode_url || "") ||
    blurb !== (c.public_blurb || "");

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      onChange(await patchCandidate(c.id, body));
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(extractApiError(err, "Could not save."));
    } finally {
      setBusy(false);
    }
  };

  const save = () => patch({ recording_at: when || "", recording_format: format, episode_url: url, public_blurb: blurb }, "Recording saved");
  const st = recordingStatus(c.recording_at);
  const visible = c.recording_at && !c.hide_from_site;

  return (
    <div data-testid="scout-recording" className="mt-4 border border-slate-200 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div data-testid="scout-recording-status" className={`inline-flex items-center gap-2 border px-3 py-1.5 text-xs font-medium ${TONE[st.tone]}`}>
          <CalendarClock className="w-3.5 h-3.5" /> {st.text}
        </div>
        <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="scout-recording-hide" checked={Boolean(c.hide_from_site)} disabled={busy}
            onChange={(e) => patch({ hide_from_site: e.target.checked }, e.target.checked ? "Hidden from the public calendar" : "Back on the public calendar")}
            className="w-4 h-4 accent-[var(--accent)]" />
          <EyeOff className="w-3.5 h-3.5" /> Hide from site
        </label>
      </div>
      {c.rescheduled_from && c.recording_at && (
        <p className="mt-2 text-xs text-amber-700" data-testid="scout-recording-rescheduled">
          Rescheduled — was {new Date(c.rescheduled_from.slice(0, 16)).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} ET.
        </p>
      )}
      <p className="mt-2 text-xs text-slate-500" data-testid="scout-recording-visibility">
        {visible ? "Live on the public Recording Calendar." : c.hide_from_site ? "Hidden — listeners won't see this session." : "Set a date and it appears on the public calendar automatically."}
      </p>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-slate-500">
          Recording date &amp; time (ET)
          <input type="datetime-local" data-testid="scout-recording-when" value={when} onChange={(e) => setWhen(e.target.value)}
            className="mt-1 block w-full border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] outline-none focus:border-[var(--accent)]" />
        </label>
        <label className="text-xs text-slate-500">
          Format
          <select data-testid="scout-recording-format" value={format} onChange={(e) => setFormat(e.target.value)}
            className="mt-1 block w-full border border-slate-300 bg-white px-3 py-2 text-sm text-[#0A192F] outline-none focus:border-[var(--accent)]">
            {FORMATS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </label>
      </div>
      <input data-testid="scout-recording-blurb" value={blurb} maxLength={400} onChange={(e) => setBlurb(e.target.value)}
        placeholder="One-line public teaser listeners will see (optional)"
        className="mt-3 w-full border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--accent)]" />
      <div className="mt-3 flex items-center gap-2">
        <Youtube className="w-4 h-4 text-slate-400 shrink-0" />
        <input data-testid="scout-recording-episode-url" value={url} onChange={(e) => setUrl(e.target.value)}
          placeholder="Recorded episode link (YouTube) — fill in after recording"
          className="w-full border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--accent)]" />
      </div>
      <button type="button" data-testid="scout-recording-save" onClick={save} disabled={!dirty || busy}
        className="mt-3 inline-flex items-center gap-2 border border-[#0A192F] hover:bg-[#0A192F] hover:text-white disabled:opacity-40 px-3 py-1.5 text-xs font-medium transition-colors">
        <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save recording"}
      </button>
    </div>
  );
};

export default RecordingPanel;
